import { useState } from "react";
import { CalendarOff, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierPickupSlot,
  type CarrierProviderAddOnService,
  useCarrierDispatchCenters,
  useCarrierPickupSlots,
  useDeleteCarrierPickupSlot,
} from "@/api/carrier";
import { PickupSlotDialog } from "@/features/pickup-slot-dialog";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/status-badge";
import { PickupDispatchGuidance } from "./pickup-dispatch-guidance";
import { hasHttpStatus } from "@/lib/api-error";
import { formatPickupServiceDays } from "@/features/pickup-service-days";

function errorMessage(error: unknown, fallback: string) {
  const response = error as { response?: { data?: { error?: string } } };
  return response.response?.data?.error ?? fallback;
}

function slotCapacity(slot: CarrierPickupSlot): string {
  const base =
    slot.defaultCapacity == null ? "Unlimited" : String(slot.defaultCapacity);
  const overrides = slot.capacityOverrides?.length ?? 0;
  return overrides
    ? `${base} · ${overrides} date override${overrides === 1 ? "" : "s"}`
    : base;
}

function SlotsEmptyState() {
  return (
    <div className="p-6 text-center">
      <CalendarOff className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />
      <p className="font-medium">No pickup slots yet.</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Customers cannot schedule this service until a booking window exists.
      </p>
    </div>
  );
}

export function PickupServiceSlots({
  service,
}: {
  service: CarrierProviderAddOnService;
}) {
  const slots = useCarrierPickupSlots(service.id);
  const dispatches = useCarrierDispatchCenters();
  const dispatchOptions = (dispatches.data ?? []).filter(
    (dispatch) => dispatch.pickupProviderCarrierId === service.providerCarrierId
  );
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CarrierPickupSlot | null>(null);
  const [deleting, setDeleting] = useState<CarrierPickupSlot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const remove = useDeleteCarrierPickupSlot();
  const slotsForbidden = hasHttpStatus(slots.error, 403);

  const deleteSlot = async (slot: CarrierPickupSlot) => {
    setError(null);
    try {
      await remove.mutateAsync({ slotId: slot.id, serviceId: service.id });
      toast.success("Pickup slot deleted.");
      setDeleting(null);
    } catch (caught) {
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to delete pickup slots."
        : errorMessage(caught, "Pickup slot could not be deleted.");
      setError(message);
      toast.error(message);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-col items-stretch justify-between gap-3 space-y-0 sm:flex-row sm:items-start">
        <div>
          <CardTitle>Pickup time slots</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Booking windows belong to this service and one dispatch center.
            Service days control when customers can book; capacity can be
            unlimited, fixed, or overridden for a specific date.
          </p>
        </div>
        <Button
          size="sm-touch"
          className="w-full sm:w-auto"
          onClick={() => setCreating(true)}
          disabled={
            dispatches.isLoading ||
            !!dispatches.error ||
            !dispatchOptions.length
          }
        >
          <Plus className="mr-1 h-4 w-4" /> New slot
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {dispatches.isLoading && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading dispatch center context…
          </p>
        )}
        {!dispatches.isLoading &&
          (dispatches.error || !dispatchOptions.length) && (
            <PickupDispatchGuidance query={dispatches} />
          )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {slots.error && (
          <div
            role="alert"
            className="flex flex-col gap-2 rounded-md border bg-destructive/5 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
          >
            <span>
              {slotsForbidden
                ? "You do not have permission to view pickup slots."
                : "Pickup slots could not be loaded."}
            </span>
            {!slotsForbidden && (
              <Button
                variant="outline"
                size="sm-touch"
                onClick={() => void slots.refetch()}
              >
                Try again
              </Button>
            )}
          </div>
        )}

        {!slotsForbidden && (!slots.error || (slots.data ?? []).length > 0) && (
          <div className="space-y-3 md:hidden">
            {slots.isLoading ? (
              <div role="status" className="p-6 text-center text-sm">
                Loading pickup slots…
              </div>
            ) : (slots.data ?? []).length === 0 ? (
              <SlotsEmptyState />
            ) : (
              (slots.data ?? []).map((slot) => (
                <div key={slot.id} className="space-y-3 rounded-md border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-medium">
                        {slot.name}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {slot.dispatchCenter?.name ?? slot.dispatchCenterId}
                      </p>
                    </div>
                    <StatusBadge
                      status={slot.isActive ? "ACTIVE" : "INACTIVE"}
                    />
                  </div>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                    <dt className="text-muted-foreground">Window</dt>
                    <dd>
                      {slot.startTime}–{slot.endTime}
                    </dd>
                    <dt className="text-muted-foreground">Service days</dt>
                    <dd>{formatPickupServiceDays(slot.serviceDays)}</dd>
                    <dt className="text-muted-foreground">Lead time</dt>
                    <dd>{slot.bookingLeadMinutes} min</dd>
                    <dt className="text-muted-foreground">Capacity</dt>
                    <dd>{slotCapacity(slot)}</dd>
                    <dt className="text-muted-foreground">Sort order</dt>
                    <dd>{slot.sortOrder}</dd>
                  </dl>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="touch"
                      onClick={() => setEditing(slot)}
                    >
                      <Pencil className="mr-2 h-4 w-4" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="touch"
                      className="text-destructive"
                      disabled={remove.isPending}
                      onClick={() => setDeleting(slot)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {!slotsForbidden && (!slots.error || (slots.data ?? []).length > 0) && (
          <div className="hidden overflow-hidden rounded-md border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Dispatch center</TableHead>
                  <TableHead>Window</TableHead>
                  <TableHead>Service days</TableHead>
                  <TableHead>Lead time</TableHead>
                  <TableHead>Capacity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slots.isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="py-10 text-center text-muted-foreground"
                    >
                      Loading pickup slots…
                    </TableCell>
                  </TableRow>
                ) : (slots.data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center">
                      <CalendarOff className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />
                      <p className="font-medium">No pickup slots yet.</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Customers cannot schedule this pickup service until a
                        booking window exists.
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  (slots.data ?? []).map((slot) => (
                    <TableRow key={slot.id}>
                      <TableCell className="font-medium">{slot.name}</TableCell>
                      <TableCell>
                        {slot.dispatchCenter?.name ?? slot.dispatchCenterId}
                      </TableCell>
                      <TableCell>
                        {slot.startTime}–{slot.endTime}
                      </TableCell>
                      <TableCell>
                        {formatPickupServiceDays(slot.serviceDays)}
                      </TableCell>
                      <TableCell>{slot.bookingLeadMinutes} min</TableCell>
                      <TableCell>
                        <div>
                          {slot.defaultCapacity == null
                            ? "Unlimited"
                            : slot.defaultCapacity}
                        </div>
                        {(slot.capacityOverrides?.length ?? 0) > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {slot.capacityOverrides?.length} date override
                            {slot.capacityOverrides?.length === 1 ? "" : "s"}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={slot.isActive ? "ACTIVE" : "INACTIVE"}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Edit ${slot.name}`}
                            onClick={() => setEditing(slot)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Delete ${slot.name}`}
                            disabled={remove.isPending}
                            onClick={() => setDeleting(slot)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {creating && (
        <PickupSlotDialog
          serviceId={service.id}
          dispatches={dispatchOptions}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <PickupSlotDialog
          key={editing.id}
          serviceId={service.id}
          dispatches={dispatchOptions}
          slot={editing}
          onClose={() => setEditing(null)}
        />
      )}

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete pickup slot?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting
                ? `“${deleting.name}” will no longer be available for pickup bookings.`
                : "This pickup slot will be deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (deleting) void deleteSlot(deleting);
              }}
            >
              {remove.isPending ? "Deleting…" : "Delete slot"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
